import * as GC from '@grapecity/spread-sheets';
import { ResultsetItem } from '@models/resultset-item';
import { COL_WIDTH, CHART_HEIGHT, ROW_HEIGHT, ROW_HEIGHT_TITLE, CHART_COLUMNS_BACK_COLOR } from './results-spread-config';
import * as SheetFunctions from '@helpers/spread/sheet-functions';
import { SettingsService } from '@services/settings/settings.service';

function calculateChartAxeSpace(liquidities, stressCount: number) {
  let maxAbsValue: number = 0;
  Object.keys(liquidities).forEach((lkey, lind) => {
    const liq = liquidities[lkey];
    const filtered = Object.keys(liq.Results);
    filtered.forEach((rkey, rind) => {
      const absRemainingBalance = Math.round(Math.abs(liq.Results[rkey].RemainingBalance));
      maxAbsValue = Math.max(maxAbsValue, absRemainingBalance);
    });
  });

  // ! Defect 4121: Touch carefully. It's magic to make the chart fit for different RemainingBalance values
  // -- Based on the characters length of the biggest number
  const len = maxAbsValue.toString().length;
  const commas = Math.floor(len > 3 ? (len / 4 > 0 ? -1 + len / 4 : 0) : 0);
  const leftAxeSpace = COL_WIDTH * 0.35;
  const commaCorrector = commas * (COL_WIDTH * 0.25);
  const trickyAdjustment = +Math.round(leftAxeSpace + commaCorrector);

  return +Math.abs(trickyAdjustment);
}

export function addChartToSheet(
  sheet: GC.Spread.Sheets.Worksheet,
  range: string,
  baseline: ResultsetItem,
  row: number,
  numPeriods: number,
  startingCol: number,
  chartCorrectorFactor: number,
  settingsService: SettingsService
): number {
  // Parameters of the chart
  const stressCount = baseline.hasOwnProperty('StressList')
    ? baseline.StressList.filter(stress => stress.Is_Stress && !stress.Hidden).length
    : 0;
  const axeSpace = calculateChartAxeSpace(baseline.Results.Liquidities, stressCount);
  const rightSpaceFit = 20;
  const size = {
    width: Math.round(axeSpace + rightSpaceFit + numPeriods * COL_WIDTH),
    height: CHART_HEIGHT,
  };
  const coords = {
    // Horizontal Position (Origin)
    x: Math.round([0, 1, 2, 3].reduce((acc, _col) => acc + sheet.getColumnWidth(_col), -axeSpace)),
    // Vertical Position (Origin)
    y: Math.round(row * ROW_HEIGHT + chartCorrectorFactor * (ROW_HEIGHT_TITLE - ROW_HEIGHT)),
  };

  // Adding the CHART
  let chart: GC.Spread.Sheets.Charts.Chart = sheet.charts.add(
    baseline.Mnemonic,
    GC.Spread.Sheets.Charts.ChartType.columnClustered,
    coords.x,
    coords.y,
    size.width,
    size.height
  );

  // Adding SERIES to the chart
  const serRows = range.split(',').map(i => Number(i));
  serRows.forEach((rowIndex, loopIndex) => {
    const scenario = loopIndex === 0 ? baseline : baseline.StressList.filter(s => !s.Hidden)[loopIndex - 1];
    const scenarioColor = settingsService.getScenarioColor(scenario);

    // It would return "E" for 12 periods displayed
    const firstPeriodColumnName = SheetFunctions.getColumnHeaderName(sheet, 2 + startingCol);
    // It would return "P" for 12 periods displayed
    const lastPeriodColumnName = SheetFunctions.getColumnHeaderName(sheet, startingCol + numPeriods + 1);
    addSeries(
      chart,
      `${sheet.name()}!$${firstPeriodColumnName}$4:$${lastPeriodColumnName}$4`,
      `${sheet.name()}!$${firstPeriodColumnName}$${rowIndex}:$${lastPeriodColumnName}$${rowIndex}`,
      `${sheet.name()}!$C$${rowIndex - 2}`,
      scenarioColor
    );
  });

  // Use Animation in charts
  chart.useAnimation(true);

  // Configure Chart Title
  const title = chart.title();
  title.text = '';
  chart.title(title);

  // Hide background
  const cArea: GC.Spread.Sheets.Charts.IChartArea = chart.chartArea();
  cArea.backColor = null;
  chart.chartArea(cArea);
  // hide grid lines
  const axes: GC.Spread.Sheets.Charts.IAxes = chart.axes();
  axes.primaryValue.majorGridLine.visible = false;
  axes.primaryValue.minorTickPosition = GC.Spread.Sheets.Charts.TickMark.outside;
  axes.primaryValue.majorTickPosition = GC.Spread.Sheets.Charts.TickMark.outside;
  axes.primaryCategory.minorUnit = 1;
  axes.primaryCategory.majorUnit = 1;
  chart.axes(axes);

  // -- Fix 3722: chart needs to be typed as any to be exported, as fromJSON / toJSON() functions are not yet in the grapecity types file.
  const genericChart: any = chart as any;
  const jsonChart = genericChart.toJSON();
  jsonChart.chartSpace.chart.plotArea.chartGroups[0].overlap = 40; // Percentage of overlap
  jsonChart.chartSpace.chart.plotArea.chartGroups[0].gapWidth = 120; // Gap Width between groups of bars (pdNum in this case)
  chart = genericChart.fromJSON(jsonChart) as GC.Spread.Sheets.Charts.Chart;
  // Increment the number of rows depending of the chart height
  row += Math.round(size.height / ROW_HEIGHT);

  return row;
}

function addSeries(chart: GC.Spread.Sheets.Charts.Chart, xValues: string, yValues: string, name: string, resultsetColor: string) {
  const serItem: GC.Spread.Sheets.Charts.ISeries = {
    axisGroup: 0,
    xValues,
    yValues,
    name,
    backColor: resultsetColor || CHART_COLUMNS_BACK_COLOR,
    // chart bar's borders
    border: {
      color: '#8c8c8c',
      width: 1,
      transparency: 0,
    },
  };

  chart.series().add(serItem);
}
